import { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { faBuildings } from '@fortawesome/pro-solid-svg-icons'
import { observer } from 'mobx-react'

import { GLOBAL_ROUTE_PATHS } from 'Router/Router.constants'

import { DashboardDevices } from 'Modules/dashboard/components/DashboardDevices/DashboardDevices'
import { DashboardServiceAppTitle } from 'Modules/dashboard/components/DashboardServiceAppTitle/DashboardServiceAppTitle'
import { NewJoinerRedirect } from 'Modules/dashboard/components/NewJoinerRedirect/NewJoinerRedirect'
import { getNewCaseItems } from 'Modules/dashboard/utils/getNewCaseItems.utils'

import { Heading, PrimaryButton, Text } from 'Components/_theme'
import { FaqCard } from 'Components/FaqCard/FaqCard'
import { Icon } from 'Components/Icon/Icon'
import { PageErrorState } from 'Components/PageErrorState/PageErrorState'
import { PageLoadingState } from 'Components/PageLoadingState/PageLoadingState'

import { useStore } from 'Stores/index'
import { UserModeEnum } from 'Stores/portalSettingsStore/portalSettingsStore.types'

import { useActiveUser, useIsServiceApp, useTheme } from 'Hooks'

import type {
  getEmployeeDashboard,
  getEmployeeDashboardVariables,
} from 'Constants/graphql/queries/employee/__generated__/getEmployeeDashboard'
import { GET_EMPLOYEE_DASHBOARD } from 'Constants/graphql/queries/employee/getEmployee'
import { PortalSectionEnum } from 'Constants/portalSection'

import decor from 'Assets/images/layout/employee-dashboard-decor.svg'
import decorTelekom from 'Assets/images/layout/employee-dashboard-decor-telekom.svg'

import { composePath } from 'Portal/src/utilities/composePath/composePath.util'

import { DashboardEmployeeBanner } from '../DashboardEmployeeBanner/DashboardEmployeeBanner'

export const EmployeeDashboardDefault = observer(() => {
  const {
    portalSettingsStore,
    orderStore,
    rentalStore,
    portalConfigStore,
    employeeStore,
    portalContentStore,
  } = useStore()
  const { setCurrentPortalSection, userMode } = portalSettingsStore
  const { isOrderDisabled } = orderStore
  const { portalConfig } = portalConfigStore
  const { portalContent } = portalContentStore
  const { employeeError } = employeeStore
  const { rentalDetails, setRentalDetails } = rentalStore
  const { activeEmployee } = useActiveUser()
  const navigate = useNavigate()
  const { employeeId } = useParams<{ employeeId: string }>()

  const [isNewOrderButtonShown, setIsNewOrderButtonShown] = useState(false)

  const isServiceApp = useIsServiceApp()

  const { isTelekomTheme } = useTheme()

  const {
    data,
    loading: isLoading,
    error,
    refetch,
  } = useQuery<getEmployeeDashboard, getEmployeeDashboardVariables>(
    GET_EMPLOYEE_DASHBOARD,
    {
      errorPolicy: 'all',
      variables: { id: activeEmployee?.id || '' },
    },
  )

  const hasAnyDashboardCustomization = !!useMemo(() => {
    return (
      portalContent?.dashboardHeaderEN ||
      portalContent?.dashboardHeaderDE ||
      portalContent?.dashboardSubtitleEN ||
      portalContent?.dashboardSubtitleDE ||
      portalContent?.dashboardButtonEN ||
      portalContent?.dashboardButtonDE ||
      portalContent?.dashboardImageURL
    )
  }, [portalContent])

  useEffect(() => {
    setCurrentPortalSection(PortalSectionEnum.Dashboard)
  }, [setCurrentPortalSection])

  useEffect(() => {
    const canOrderNewDevice =
      !!activeEmployee?.canOrderNewDevice &&
      !!portalConfig?.newDevice &&
      !!activeEmployee?.canOrder

    const canPurchaseNewDevice =
      !!activeEmployee?.canOrderNewDevice &&
      !!portalConfig?.newDevice &&
      !!portalConfig?.enableProductOffers &&
      !!activeEmployee?.canPurchase

    const newSmartphoneButtonIsVisible =
      canOrderNewDevice || canPurchaseNewDevice

    setIsNewOrderButtonShown(newSmartphoneButtonIsVisible)
  }, [
    portalConfig?.newDevice,
    activeEmployee,
    portalConfig?.enableProductOffers,
  ])

  useEffect(() => {
    if (rentalDetails && userMode === UserModeEnum.DEFAULT) {
      setRentalDetails(null)
    }
  }, [rentalDetails, setRentalDetails, userMode])

  const handleNewOrderButtonClick = useCallback(() => {
    navigate(
      composePath(
        {
          params: { employeeId },
          path: GLOBAL_ROUTE_PATHS.employee.orderNew,
        },
        isNewOrderButtonShown ? {} : { order: 'disabled' },
      ),
    )
  }, [employeeId, isNewOrderButtonShown, navigate])

  if (userMode === UserModeEnum.NEW_JOINER) {
    const employeeNewDeviceDisabled =
      !activeEmployee?.canOrderNewDevice ||
      isOrderDisabled ||
      !portalConfig?.newDevice

    if (employeeNewDeviceDisabled) {
      return <PageErrorState />
    }

    return <NewJoinerRedirect />
  }

  if (error || employeeError) {
    return <PageErrorState />
  }

  if (isLoading || !data) {
    return <PageLoadingState />
  }

  const { employee } = data
  const rentals = employee.rentals?.rentals
  let devicesCount = rentals?.length ?? 0

  if (employee.caseitems?.caseItems) {
    devicesCount += getNewCaseItems(employee.caseitems?.caseItems).length
  }

  return (
    <Container className="EmployeeDashboard dashboard relative" fluid>
      <img
        alt=""
        className="position-absolute top-0 end-0 d-none d-xxl-inline"
        src={isTelekomTheme ? decorTelekom : decor}
      />
      <Row className="position-relative">
        {isServiceApp ? (
          <Col role="banner">
            <DashboardServiceAppTitle
              devicesCount={devicesCount}
              handleRentDeviceButtonClick={handleNewOrderButtonClick}
              showRentDeviceButton={isNewOrderButtonShown}
            />
          </Col>
        ) : (
          <Col
            className="pb-6 py-lg-6 d-flex flex-column flex-lg-row justify-content-between"
            xs={12}
            xxl={8}
          >
            <div className="d-flex justify-content-center justify-content-lg-start text-center text-lg-start">
              <div className="EmployeeDashboard-logo d-none d-lg-flex align-items-center justify-content-center shadow-sm">
                {activeEmployee?.organisation?.logoImageURL ? (
                  <img
                    alt={activeEmployee.organisation.fullName}
                    height={60}
                    src={activeEmployee.organisation.logoImageURL}
                    width={60}
                  />
                ) : (
                  <Icon icon={faBuildings} size={60} />
                )}
              </div>
              <div className="d-flex flex-column justify-content-center mx-lg-6">
                <Text className="EmployeeDashboard-organisation-name text-dark">
                  {activeEmployee?.organisation?.fullName}
                </Text>
                <Heading as={3} className="text-dark mt-2" size={1}>
                  <FormattedMessage
                    id="Dashboard_greeting"
                    values={{ name: activeEmployee?.contact.firstName }}
                  />
                </Heading>
              </div>
            </div>
            {isNewOrderButtonShown &&
              devicesCount > 0 &&
              !hasAnyDashboardCustomization && (
                <PrimaryButton
                  className="EmployeeDashboard-new-device align-self-center mt-4 mt-lg-0"
                  onClick={handleNewOrderButtonClick}
                >
                  <FormattedMessage id="New_device" />
                </PrimaryButton>
              )}
          </Col>
        )}
      </Row>

      <Row className="position-relative">
        <Col xs={12} xxl={8}>
          {(devicesCount === 0 || hasAnyDashboardCustomization) && (
            <DashboardEmployeeBanner
              onNewOrderButtonClick={handleNewOrderButtonClick}
              version={isServiceApp ? 'serviceApp' : 'employee'}
            />
          )}

          <DashboardDevices
            caseItems={employee.caseitems}
            header="DevicesCard_devices_header"
            refetch={refetch}
            renderServiceApp
            rentals={rentals}
          />
        </Col>

        <Col xs={12} xxl={4}>
          <Row>
            <Col xs={12}>
              <FaqCard showOnMobile />
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  )
})
